import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { selectFormsDataState } from '../store/forms-data/forms-data.selectors';
import { selectNavigationState } from '../store/navigation/navigation.selectors';
import { selectErrorResponse } from '../store/api-error/api-error.selectors';
import {
  changeConfirmFormData,
  changeDeliveryCourierFormData,
  changeDeliveryPointFormData,
  changeShipmentFormData,
  changeShippingCourierFormData,
  changeShippingPointFormData,
  changeSelectedPartners,
} from '../store/forms-data/forms-data.actions';
import { changeErrorResponse } from '../store/api-error/api-error.actions';
import { CaptchaResponse } from '../models/main-page/captcha-response.model';
import { PreAuthRequest } from '../models/pre-auth/pre-auth-request.model';
import { PreAuthResponse } from '../models/pre-auth/pre-auth-response.model';
import { ShipmentFormData } from '../models/shipment/shipment-form-data.model';
import { DeliveryPointFormData } from '../models/delivery-courier/delivery-point-form-data.model';
import { DeliveryCourierFormData } from '../models/delivery-courier/delivery-courier-form-data.model';
import { OrderParty } from '../models/pre-auth/order-party.model';
import { PickupResponse } from '../models/pickup/pickup-response.model';
import { ConfirmFormData } from '../models/confirm-data/confirm-form-data.model';
import { ShippingCourierFormData } from '../models/shipping-courier/shipping-courier-form-data.model';
import { PickupDateHoursPeriodResponse } from '../models/pickup-date-hours-period/pickup-date-hours-period-response.model';
import { ShippingPointFormData } from '../models/shipping-courier/shipping-point-form-data.model';
import { PreAuthData } from '../models/pre-auth/pre-auth-data.model';
import { ErrorResponse } from '../models/error/error-response.model';

// Enums
import {
  NadajPrzesylkeType,
  ScreenType,
  SposobDoreczeniaType,
  SposobNadaniaType,
} from '../enums/enums';
import {
  selectApiDataState,
  selectPreAuthResponse,
} from '../store/api-data/api-data.selectors';
import {
  changeCaptchaResponse,
  changePreAuthRequest,
  changePreAuthResponse,
  changePickupResponse,
  changePickupDateHoursPeriodResponse,
} from '../store/api-data/api-data.actions';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  private store = inject(Store);
  private navigationSignal = toSignal(this.store.select(selectNavigationState));
  private formsDataSignal = toSignal(this.store.select(selectFormsDataState));
  private apiDataSignal = toSignal(this.store.select(selectApiDataState));
  private errorResponseSignal = toSignal(
    this.store.select(selectErrorResponse)
  );
  private preAuthResponseSignal = toSignal(
    this.store.select(selectPreAuthResponse)
  );

  // Getters
  public getTokenJwt(): string | undefined {
    return this.preAuthResponseSignal()?.tokenJwt;
  }

  public getPointsAuthToken(): string | undefined {
    return this.preAuthResponseSignal()?.pointsAuthToken;
  }

  public getSender(): OrderParty | undefined {
    return this.preAuthResponseSignal()?.sender;
  }

  public getIsOkForPoint(): boolean | undefined {
    return this.preAuthResponseSignal()?.isOkForPoint;
  }

  public getCptId(): number | undefined {
    return this.apiDataSignal()?.captchaResponse?.cptId;
  }

  public getCaptchaB64(): string | undefined {
    return this.apiDataSignal()?.captchaResponse?.captchaB64;
  }

  public getNadajPrzesylkeTyp(): NadajPrzesylkeType | undefined {
    return this.navigationSignal()?.nadajPrzesylkeTyp;
  }

  public getSposobNadaniaStepNumber(): number | undefined {
    return this.navigationSignal()?.sposobNadaniaStepNumber;
  }

  public getSposobNadaniaTyp(): SposobNadaniaType | undefined {
    return this.navigationSignal()?.sposobNadaniaTyp;
  }

  public getSposobDoreczeniaTyp(): SposobDoreczeniaType | undefined {
    return this.navigationSignal()?.sposobDoreczeniaTyp;
  }

  public getShipNo(): string | undefined {
    return this.apiDataSignal()?.pickupResponse?.shipNo;
  }

  public getPckNo(): string | undefined {
    return this.apiDataSignal()?.pickupResponse?.pckNo;
  }

  public getPickupResponse(): PickupResponse | undefined {
    return this.apiDataSignal()?.pickupResponse;
  }

  public getPreAuthData(): PreAuthData | undefined {
    return this.apiDataSignal()?.preAuthRequest?.preAuthData;
  }

  public getZipCode(): string | undefined {
    return this.apiDataSignal()?.preAuthRequest?.preAuthData.zipCode;
  }

  public getPayerNo(): number | undefined {
    return this.apiDataSignal()?.preAuthRequest?.preAuthData.payerNo;
  }

  public getScreenType(): ScreenType | undefined {
    return this.navigationSignal()?.screen;
  }

  public getShipmentFormData(): ShipmentFormData | undefined {
    return this.formsDataSignal()?.shipmentFormData;
  }

  public getConfirmFormData(): ConfirmFormData | undefined {
    return this.formsDataSignal()?.confirmFormData;
  }

  public getSelectedPartners(): string[] | undefined {
    return this.formsDataSignal()?.selectedPartners;
  }

  public getShippingCourierFormData(): ShippingCourierFormData | undefined {
    return this.formsDataSignal()?.shippingCourierFormData;
  }

  public getShippingPointFormData(): ShippingPointFormData | undefined {
    return this.formsDataSignal()?.shippingPointFormData;
  }

  public getPickupDateHoursPeriodResponse():
    | PickupDateHoursPeriodResponse
    | undefined {
    return this.apiDataSignal()?.pickupDateHoursPeriodResponse;
  }

  public getDeliveryCourierFormData(): DeliveryCourierFormData | undefined {
    return this.formsDataSignal()?.deliveryCourierFormData;
  }

  public getDeliveryPointFormData(): DeliveryPointFormData | undefined {
    return this.formsDataSignal()?.deliveryPointFormData;
  }

  public getErrorResponse(): ErrorResponse | undefined {
    return this.errorResponseSignal();
  }

  public getNavigationSignal() {
    return this.store.select(selectNavigationState);
  }

  // Setters
  public setCaptchaResponse(captchaResponse: CaptchaResponse | undefined) {
    this.store.dispatch(changeCaptchaResponse({ captchaResponse }));
  }

  public setPreAuthRequest(preAuthRequest: PreAuthRequest | undefined) {
    this.store.dispatch(changePreAuthRequest({ preAuthRequest }));
  }

  public setPreAuthResponse(preAuthResponse: PreAuthResponse | undefined) {
    this.store.dispatch(changePreAuthResponse({ preAuthResponse }));
  }

  public setShipmentFormData(shipmentFormData: ShipmentFormData | undefined) {
    this.store.dispatch(changeShipmentFormData({ shipmentFormData }));
  }

  public setPickupErrorResponse(errorResponse: ErrorResponse | undefined) {
    this.store.dispatch(changeErrorResponse({ errorResponse }));
  }

  public setDeliveryPointFormData(
    deliveryPointFormData: DeliveryPointFormData | undefined
  ) {
    this.store.dispatch(changeDeliveryPointFormData({ deliveryPointFormData }));
  }

  public setDeliveryCourierFormData(
    deliveryCourierFormData: DeliveryCourierFormData | undefined
  ) {
    this.store.dispatch(
      changeDeliveryCourierFormData({ deliveryCourierFormData })
    );
  }

  public setConfirmFormData(confirmFormData: ConfirmFormData | undefined) {
    this.store.dispatch(changeConfirmFormData({ confirmFormData }));
  }

  public setSelectedPartners(selectedPartners: string[]) {
    this.store.dispatch(changeSelectedPartners({ selectedPartners }));
  }

  public setPickupResponse(pickupResponse: PickupResponse | undefined) {
    this.store.dispatch(changePickupResponse({ pickupResponse }));
  }

  public setShippingPointFormData(
    shippingPointFormData: ShippingPointFormData | undefined
  ) {
    this.store.dispatch(changeShippingPointFormData({ shippingPointFormData }));
  }

  public setShippingCourierFormData(
    shippingCourierFormData: ShippingCourierFormData | undefined
  ) {
    this.store.dispatch(
      changeShippingCourierFormData({ shippingCourierFormData })
    );
  }

  public setPickupDateHoursPeriodResponse(
    pickupDateHoursPeriodResponse: PickupDateHoursPeriodResponse | undefined
  ) {
    this.store.dispatch(
      changePickupDateHoursPeriodResponse({ pickupDateHoursPeriodResponse })
    );
  }

  // Utilities
  public resetCache() {
    this.store.dispatch(changePreAuthResponse({ preAuthResponse: undefined }));
    this.store.dispatch(
      changePickupDateHoursPeriodResponse({
        pickupDateHoursPeriodResponse: undefined,
      })
    );
    this.store.dispatch(
      changeDeliveryCourierFormData({ deliveryCourierFormData: undefined })
    );
    this.store.dispatch(
      changeShipmentFormData({ shipmentFormData: undefined })
    );
    this.store.dispatch(
      changeShippingCourierFormData({ shippingCourierFormData: undefined })
    );
    this.store.dispatch(
      changeShippingPointFormData({ shippingPointFormData: undefined })
    );
    this.store.dispatch(
      changeDeliveryPointFormData({ deliveryPointFormData: undefined })
    );
    this.store.dispatch(changePickupResponse({ pickupResponse: undefined }));
    this.store.dispatch(changeConfirmFormData({ confirmFormData: undefined }));
    this.store.dispatch(changeErrorResponse({ errorResponse: undefined }));
    this.store.dispatch(changeSelectedPartners({ selectedPartners: [] }));
  }

  public isDeliveryPickupPointPosible(): boolean {
    if (this.getSender() !== undefined) {
      if (this.getIsOkForPoint() === true) {
        return true;
      } else {
        return false;
      }
    }

    const shipmentFormData = this.getShipmentFormData();
    if ((shipmentFormData?.palettes?.length ?? 0) > 0) {
      return false;
    }

    if ((shipmentFormData?.parcels?.length ?? 0) > 1) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.isNotStandard ?? false) === true) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.count ?? 0) > 1) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.weight ?? 0) > 20) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.length ?? 0) > 50) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.width ?? 0) > 40) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.height ?? 0) > 30) {
      return false;
    }
    if ((shipmentFormData?.codAmount ?? 0) > 3000) {
      return false;
    }
    if ((shipmentFormData?.insAmount ?? 0) > 5000) {
      return false;
    }
    return true;
  }

  public isShippingPickupPointPosible(): boolean {
    if (this.getSender() !== undefined) {
      if (this.getIsOkForPoint() === true) {
        return true;
      } else {
        return false;
      }
    }

    const shipmentFormData = this.getShipmentFormData();
    if ((shipmentFormData?.palettes?.length ?? 0) > 0) {
      return false;
    }

    if ((shipmentFormData?.parcels?.length ?? 0) > 1) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.isNotStandard ?? false) === true) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.count ?? 0) > 1) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.weight ?? 0) > 20) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.length ?? 0) > 50) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.width ?? 0) > 40) {
      return false;
    }
    if ((shipmentFormData?.parcels?.[0]?.height ?? 0) > 30) {
      return false;
    }
    return true;
  }
}
